import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface SegmentOption<T extends string> {
  value: T
  label: string
  /** A shorter visible label on narrow screens; accessible name stays descriptive. */
  shortLabel?: string
  /** A small count beside the label (unread mail, items needing the person). */
  count?: number
  icon?: ReactNode
}

interface SegmentedProps<T extends string> {
  options: SegmentOption<T>[]
  value: T
  onChange: (value: T) => void
  size?: 'sm' | 'md'
  'aria-label'?: string
  /** Stretch to the container, each option an equal share (phone view switches). */
  fullWidth?: boolean
  className?: string
}

/**
 * A 2–5 way switch: the wizard's Yes / No / Prefer-not, the tracker's
 * Inbox / Pipeline / Applications. A radiogroup, so arrow keys and screen
 * readers work. Options may carry a `count` badge.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  'aria-label': ariaLabel,
  fullWidth,
  className,
}: SegmentedProps<T>) {
  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    const delta = event.key === 'ArrowRight' ? 1 : -1
    const next = options[(index + delta + options.length) % options.length]
    if (next) {
      onChange(next.value)
      const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role=radio]')
      buttons?.[(index + delta + options.length) % options.length]?.focus()
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'shrink-0 overflow-hidden rounded-[10px] border border-line bg-surface-muted p-0.5',
        fullWidth ? 'grid w-full auto-cols-fr grid-flow-col' : 'inline-flex',
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.label}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[8px] font-medium transition-all',
              size === 'sm' ? 'min-h-[30px] px-2.5 py-1 text-[12.5px]' : 'min-h-[36px] px-3.5 py-1.5 text-[13.5px]',
              selected
                ? 'bg-surface text-brand shadow-card ring-1 ring-line'
                : 'text-ink-muted hover:text-ink-soft',
            )}
          >
            {option.icon}
            {option.shortLabel ? <><span className="sm:hidden">{option.shortLabel}</span><span className="hidden sm:inline">{option.label}</span></> : option.label}
            {typeof option.count === 'number' && option.count > 0 && (
              <span className={cn('num rounded-pill px-1.5 text-[11px] leading-[18px]', selected ? 'bg-brand text-white' : 'bg-ink/[0.06] text-ink-muted')}>
                {option.count > 99 ? '99+' : option.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

interface YesNoProps {
  value: boolean
  onChange: (value: boolean) => void
  'aria-label'?: string
}

export function YesNo({ value, onChange, 'aria-label': ariaLabel }: YesNoProps) {
  return (
    <Segmented
      aria-label={ariaLabel}
      options={[
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ]}
      value={value ? 'yes' : 'no'}
      onChange={(next) => onChange(next === 'yes')}
    />
  )
}

interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  disabled?: boolean
  /** `sm` for a switch that sits inside a row of secondary options. */
  size?: 'sm' | 'md'
}

export function Switch({ checked, onChange, label, disabled, size = 'md' }: SwitchProps) {
  const small = size === 'sm'
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative shrink-0 rounded-pill transition-colors',
        small ? 'h-5 w-9' : 'h-6 w-11',
        checked ? 'bg-brand' : 'bg-line-strong',
        disabled && 'cursor-not-allowed opacity-55',
      )}
    >
      <span
        className={cn(
          'absolute left-0 top-0.5 rounded-full bg-white shadow-sm transition-transform',
          small ? 'h-4 w-4' : 'h-5 w-5',
          checked ? (small ? 'translate-x-[18px]' : 'translate-x-[22px]') : 'translate-x-0.5',
        )}
      />
    </button>
  )
}

interface RadioCardProps {
  selected: boolean
  onSelect: () => void
  title: string
  description?: string
  /** A small pill beside the title, e.g. "Recommended". */
  badge?: ReactNode
  className?: string
  /** Shown but not choosable (e.g. an option the server has not configured). */
  disabled?: boolean
}

/** The Off / Honest / Aggressive rows on the final onboarding step. */
export function RadioCard({
  selected,
  onSelect,
  title,
  description,
  badge,
  className,
  disabled,
}: RadioCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 px-5 py-4 text-left transition-colors',
        selected ? 'bg-surface-muted' : !disabled && 'hover:bg-surface-muted/60',
        disabled && 'cursor-not-allowed opacity-55',
        className,
      )}
    >
      <span
        className={cn(
          'mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border-2 transition-colors',
          selected ? 'border-ink' : 'border-line-strong',
        )}
      >
        {selected && <span className="h-2 w-2 rounded-full bg-ink" />}
      </span>
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[15px] font-medium text-ink">
          {title}
          {badge}
        </span>
        {description && (
          <span className="mt-0.5 block text-[13px] leading-relaxed text-ink-muted">
            {description}
          </span>
        )}
      </span>
    </button>
  )
}
